import { Download, Downloading, HelpOutline, Refresh, Replay, VideoCall } from "@mui/icons-material";
import {
  Avatar,
  Box, Button, Card, CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem, Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import NotConfigured from "../NotConfigured";

interface IMainClientProps {
  user: any;
  accessToken?: string;
  downloadFile: (id: string) => void;
  reUploadMedia: (id: string) => Promise<Response | void> | void;
  startMeeting: () => void;
  authorize: () => void;
  environment: string;
}

export interface IRecord {
  id?: string;
  zoomAccountId?: string;
  zoomUserId?: string;
  meetingUUID?: string;
  meetingTitle: string;
  durationSeconds: string;
  timestamp: string;
  lastUpdated: string;
  status: string;
  downloadUrl: string;
  meetingType: string;
  error?: string;
}

function formatFriendlyTimestamp(dateString: string): string {
  if (!dateString) return "-";
  if (dateString.startsWith("0001-01-01")) return "-";

  const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateString)
    ? dateString
    : `${dateString}Z`;

  const date = new Date(normalized);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || isNaN(seconds) || seconds < 0) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

const MainClient = ({
  user,
  accessToken,
  downloadFile,
  reUploadMedia,
  startMeeting,
  authorize,
  environment
}: IMainClientProps) => {
  const [records, setRecords] = useState<IRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reuploadingIds, setReuploadingIds] = useState<Set<string>>(new Set());

  // Server-side state: Keep DataGrid page at 0 internally
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [meetingTypeFilter, setMeetingTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Refetch records on filter/pagination changes
  useEffect(() => {
    if (!accessToken) return;
    fetchRecords();
  }, [accessToken, paginationModel, debouncedSearch, meetingTypeFilter, statusFilter]);

  const fetchRecords = async () => {
    setIsLoading(true);
    try {
      // Map 0-indexed frontend page model to 1-indexed for the backend
      const backendPage = paginationModel.page + 1;

      const queryParams = new URLSearchParams({
        page: backendPage.toString(),
        pageSize: paginationModel.pageSize.toString(),
        search: debouncedSearch,
        type: meetingTypeFilter === "all" ? "" : meetingTypeFilter,
        status: statusFilter === "all" ? "" : statusFilter,
      });

      const response = await fetch(`/api/zoomapp/recordings?${queryParams}`, {
        headers: { "Authorization": `Bearer ${accessToken}` },
      });

      if (response.status === 401 || response.status === 403) {
        authorize();
        return;
      }

      if (!response.ok) throw new Error("Network request failed");

      const result = await response.json();
      setRecords(result.data || []);
      setTotalRows(result.totalCount || 0);
    } catch (err) {
      console.error("Error fetching records:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReUploadClick = async (rowId: string) => {
    setReuploadingIds((prev) => new Set(prev).add(rowId));

    try {
      const response = await reUploadMedia(rowId);
      const isSuccess = !response || (response instanceof Response && response.ok);

      if (isSuccess) {
        setRecords((prevRecords) =>
          prevRecords.map((r) => {
            const rId = r.id || r.meetingUUID || `${r.zoomUserId}-${r.lastUpdated}`;
            if (rId === rowId) {
              return { ...r, status: "inqueue" };
            }
            return r;
          })
        );
      }
    } catch (err) {
      console.error("Error reuploading media:", err);
    } finally {
      setReuploadingIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: "meetingTitle",
      headerName: "Topic",
      flex: 1.2,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "timestamp",
      headerName: "Recorded At",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatFriendlyTimestamp(params.value),
    },
    {
      field: "durationSeconds",
      headerName: "Duration",
      flex: 0.8,
      sortable: false,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatDuration(params.value),
    },
    {
      field: "meetingType",
      headerName: "Type",
      flex: 0.8,
      sortable: false,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => {
        switch (params.value) {
          case "ZoomPhone": return "Phone";
          case "ZoomMeeting": return "Meeting";
          default: return "-";
        }
      },
    },
    {
      field: "lastUpdated",
      headerName: "Last Updated",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatFriendlyTimestamp(params.value),
    },
    {
      field: "status",
      headerName: "Upload Status",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        const getStatusLabel = (status: string) => {
          switch (status) {
            case "inprogress": return "In Progress";
            case "failed": return "Failed";
            case "ready": return "Completed";
            case "notstarted": return "Not Uploaded";
            case "inqueue": return "Queued";
            default: return "-";
          }
        };

        const label = getStatusLabel(params.value);

        if (params.value === "failed") {
          const errorMessage = params.row.error;
          const icon = (
            <HelpOutline
              fontSize="small"
              sx={{
                color: "error.main",
                fontSize: 18,
                cursor: errorMessage ? "pointer" : "default",
              }}
            />
          );

          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>{label}</span>
              {errorMessage ? (
                <Tooltip title={errorMessage} arrow placement="top">
                  {icon}
                </Tooltip>
              ) : (
                icon
              )}
            </Box>
          );
        }

        return label;
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.meetingType !== "ZoomPhone") {
          return "-";
        }

        const rowId = params.row.id || params.row.meetingUUID || `${params.row.zoomUserId}-${params.row.lastUpdated}`;
        const isInProgress = params.row.status === "inprogress";
        const isInQueue = params.row.status === "inqueue";
        const isReuploading = reuploadingIds.has(rowId);
        const isDisabled = isInProgress || isInQueue || isReuploading;

        const getTooltipTitle = () => {
          if (isReuploading) return "Reuploading...";
          if (isInProgress) return "Reupload disabled while in progress";
          if (isInQueue) return "Reupload disabled while in queue";
          return "Reupload";
        };

        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {params.row.downloadUrl ? (
              <IconButton
                onClick={() => downloadFile(rowId)}
                title="Download"
              >
                <Download color="primary" />
              </IconButton>
            ) : (
              <IconButton disabled sx={{ color: "grey.500" }}>
                <Downloading />
              </IconButton>
            )}

            <Tooltip title={getTooltipTitle()} arrow placement="top">
              <span>
                <IconButton
                  disabled={isDisabled}
                  onClick={() => handleReUploadClick(rowId)}
                  sx={{
                    position: "relative",
                    ...(isDisabled && {
                      opacity: 0.5,
                      bgcolor: "rgba(255, 255, 255, 0.04)",
                      border: "1px dashed rgba(255, 255, 255, 0.2)",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.04)",
                      },
                    }),
                  }}
                >
                  {isReuploading ? (
                    <CircularProgress size={20} sx={{ color: "#61dafb" }} />
                  ) : (
                    <Replay color={isDisabled ? "disabled" : "primary"} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (user?.isConfigured) {
    return (
      <Stack spacing={2} alignItems="center">
        <Card
          sx={{
            color: "#fff",
            width: "90%",
            bgcolor: "rgba(255, 255, 255, 0.03)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 3, p: 3, pb: "24px !important" }}>
            <Avatar
              src={user?.pic_url && user.pic_url.trim() !== "" ? user.pic_url : undefined}
              sx={{
                width: 64,
                height: 64,
                bgcolor: "#61dafb",
                color: "#1F1D2B",
                fontWeight: "bold",
                fontSize: "1.5rem",
              }}
            >
              {(!user?.pic_url || user.pic_url.trim() === "") && (user?.display_name?.charAt(0)?.toUpperCase() || "U")}
            </Avatar>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                {user?.display_name || "Zoom User"}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.6)", mb: 1.5 }}>
                {user?.email}
              </Typography>

              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  label={user?.type === 2 ? "Licensed" : "Basic"}
                  sx={{
                    bgcolor: user?.type === 2 ? "rgba(97, 218, 251, 0.1)" : "rgba(255, 255, 255, 0.05)",
                    color: user?.type === 2 ? "#61dafb" : "white",
                    border: "1px solid",
                    borderColor: user?.type === 2 ? "rgba(97, 218, 251, 0.3)" : "rgba(255, 255, 255, 0.2)",
                    fontWeight: 500,
                  }}
                />
                <Chip
                  size="small"
                  label="Configured"
                  sx={{
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                    color: "#4caf50",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                    fontWeight: 500,
                  }}
                />
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />

            <Box sx={{ textAlign: "center", px: 2 }}>
              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.5)", mb: 0.5 }}>
                Total Recordings
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: "white" }}>
                {totalRows}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ width: "90%" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} sx={{ flexGrow: 1, mr: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  width: "220px",
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: 1,
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.4)" },
                    "&.Mui-focused fieldset": { borderColor: "#61dafb" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={meetingTypeFilter}
                  onChange={(e) => {
                    setMeetingTypeFilter(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                  displayEmpty
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.4)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#61dafb",
                    },
                    "& .MuiSvgIcon-root": { color: "white" },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#1F1D2B",
                        color: "white",
                        "& .MuiMenuItem-root": {
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
                          "&.Mui-selected": { bgcolor: "rgba(255, 255, 255, 0.16)" },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="ZoomMeeting">Meeting</MenuItem>
                  <MenuItem value="ZoomPhone">Phone</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                  displayEmpty
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.4)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#61dafb",
                    },
                    "& .MuiSvgIcon-root": { color: "white" },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#1F1D2B",
                        color: "white",
                        "& .MuiMenuItem-root": {
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
                          "&.Mui-selected": { bgcolor: "rgba(255, 255, 255, 0.16)" },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="ready">Completed</MenuItem>
                  <MenuItem value="inprogress">In Progress</MenuItem>
                  <MenuItem value="inqueue">Queued</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>

              <IconButton
                onClick={fetchRecords}
                disabled={isLoading}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                }}
                title="Refresh Table"
              >
                <Refresh />
              </IconButton>
            </Stack>

            <Tooltip title="Start a zoom meeting with the verbatim bot." arrow placement="top">
              <span>
                <Button
                  variant="contained"
                  endIcon={<VideoCall />}
                  onClick={startMeeting}
                  sx={{
                    bgcolor: "rgb(31, 29, 100)",
                    color: "white",
                    fontWeight: "600",
                    fontSize: "14px",
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(31, 29, 100, 0.4)",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "rgb(41, 39, 120)",
                      boxShadow: "0 6px 16px rgba(31, 29, 100, 0.6)",
                    },
                  }}
                >
                  Start Meeting
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <Box sx={{ height: "60vh", width: "100%" }}>
            <DataGrid
              rows={records}
              columns={columns}
              getRowId={(row) => row.id || row.meetingUUID || `${row.zoomUserId}-${row.lastUpdated}`}
              loading={isLoading}
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 30]}
              disableRowSelectionOnClick
              sx={{
                color: "white",
                bgcolor: "#1F1D2B",
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell": { color: "white" },
                "& .MuiDataGrid-footerContainer": { color: "white" },
                "& .MuiTablePagination-root": { color: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
                "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                  color: "grey !important",
                  opacity: 0.4,
                },
              }}
            />
          </Box>
        </Box>
      </Stack>
    );
  }

  return <NotConfigured user={user} />;
};

export default MainClient;